// MarketInsightSummary.tsx
// AI-powered market summary panel. Auto-fetches on mount via ai-narrative edge function.
// Returns structured signal + 3 insights + optional risk/opportunity flags.
// Uses callAiNarrative (server-side proxy) — no API key exposed to client.

import { useState, useEffect } from 'react'
import { callAi, aiErrorMessage, type AiCallError } from '../../lib/aiCall'
import type { HtagSummary, AiIntelligenceSummary, PropertyFinancialContext } from '../../lib/htag-types'

interface MarketInsightSummaryProps {
  htag_summary: HtagSummary
  property_financials: PropertyFinancialContext
  property_type_label: string
  lga_name: string
  onSignalResolved?: (signal: string) => void
}

// ─── Signal badge styles ──────────────────────────────────────────────────────

interface SignalStyle {
  background: string
  color: string
  dot: string
}

function signalStyle(signal: string): SignalStyle {
  switch (signal) {
    case 'HOLD':       return { background: '#D1FAE5', color: '#065F46', dot: '#16A34A' }
    case 'ACCUMULATE': return { background: '#DBEAFE', color: '#1E40AF', dot: '#2563EB' }
    case 'WATCH':      return { background: '#FEF3C7', color: '#92400E', dot: '#D97706' }
    case 'SELL':       return { background: '#FEE2E2', color: '#991B1B', dot: '#DC2626' }
    default:           return { background: '#F3F4F6', color: '#374151', dot: '#6B7280' }
  }
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>Analysing market data...</div>
      {[100, 85, 70].map((w, i) => (
        <div
          key={i}
          style={{
            height: 12,
            width: `${w}%`,
            background: '#F3F4F6',
            borderRadius: 4,
            marginBottom: 8,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MarketInsightSummary({
  htag_summary,
  property_financials,
  property_type_label,
  lga_name,
  onSignalResolved,
}: MarketInsightSummaryProps) {
  const [summary, setSummary] = useState<AiIntelligenceSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<AiCallError | null>(null)
  const [showRaw, setShowRaw] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setSummary(null)
    setShowRaw(false)

    const payload = {
      lga: lga_name,
      property_type: property_type_label,
      period: htag_summary.meta.period_end,
      cycle_phase: htag_summary.cycle.phase,
      growth_projection: { low: htag_summary.cycle.growth_low, high: htag_summary.cycle.growth_high },
      rcs_scores: htag_summary.scores,
      cagr: {
        price_1y: htag_summary.growth.price_1y,
        price_5y: htag_summary.growth.price_5y,
        price_10y: htag_summary.growth.price_10y,
        rent_1y: htag_summary.growth.rent_1y,
      },
      demand: htag_summary.demand,
      supply: htag_summary.supply,
      risk: htag_summary.risk,
      property_financials,
    }

    const systemPrompt = `You are a property investment analyst for AusProp, an Australian property portfolio intelligence platform. You analyse suburb market data and provide clear, direct investment insights tailored to the specific property the user owns.
You must respond with ONLY a valid JSON object. No markdown, no preamble, no explanation outside the JSON.
Response schema:
{"signal":"HOLD"|"WATCH"|"SELL"|"ACCUMULATE","signal_rationale":"one sentence explaining the signal","insights":["insight 1","insight 2","insight 3"],"risk_flag":"string or null","opportunity":"string or null"}
Rules:
- signal must be exactly one of: HOLD, WATCH, SELL, ACCUMULATE
- insights must be exactly 3 strings, each under 120 characters
- Use plain English — no jargon, no acronyms without explanation
- Be direct and specific to the numbers provided
- risk_flag: only include if there is a genuine risk worth flagging (vacancy > 3%, falling cycle, low RCS < 30, etc.)
- opportunity: only include if there is a genuine positive signal worth highlighting
- Do not mention HTAG or the data source
- Do not give financial advice disclaimers — the user knows this is informational`

    void callAi<AiIntelligenceSummary>({
      systemPrompt,
      userPrompt: JSON.stringify(payload),
      maxTokens: 600,
      parseJson: true,
    }).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setSummary(result.data)
        onSignalResolved?.(result.data.signal)
      } else {
        setError(result.error)
      }
      setLoading(false)
    })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htag_summary.meta.lga_id, htag_summary.meta.property_type])

  return (
    <div style={{
      background: '#FFFBF5',
      border: '1px solid #E8D5B7',
      borderLeft: '3px solid #C8761A',
      borderRadius: 10,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      padding: '18px 20px',
    }}>
      {loading && <Skeleton />}

      {error && !loading && (
        <div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{aiErrorMessage(error)}</div>
          {error.rawResponse && (
            <button
              type="button"
              onClick={() => setShowRaw((v) => !v)}
              style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {showRaw ? 'Hide details' : 'Show details'}
            </button>
          )}
          {showRaw && error.rawResponse && (
            <pre style={{ fontSize: 10, color: '#6B7280', background: '#F9FAFB', padding: 8, borderRadius: 6, marginTop: 4, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {error.rawResponse.slice(0, 1000)}
            </pre>
          )}
        </div>
      )}

      {summary && !loading && (
        <div>
          {/* Signal badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            {(() => {
              const s = signalStyle(summary.signal)
              return (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: s.background,
                  color: s.color,
                  fontSize: 14,
                  fontWeight: 500,
                  padding: '4px 12px',
                  borderRadius: 20,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
                  {summary.signal}
                </span>
              )
            })()}
          </div>

          {/* Signal rationale */}
          <p style={{ fontSize: 12, color: '#6B6560', margin: '0 0 14px 0', lineHeight: 1.5 }}>
            {summary.signal_rationale}
          </p>

          {/* 3 insights */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {summary.insights.map((insight, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#C8761A',
                  flexShrink: 0,
                  marginTop: 5,
                  display: 'inline-block',
                }} />
                <span style={{ fontSize: 13, color: '#1A1A1A', lineHeight: 1.55 }}>{insight}</span>
              </div>
            ))}
          </div>

          {/* Risk flag */}
          {summary.risk_flag && (
            <div style={{
              borderLeft: '3px solid #DC2626',
              background: '#FEF2F2',
              padding: '8px 12px',
              borderRadius: '0 6px 6px 0',
              marginBottom: 8,
              fontSize: 12,
              color: '#7F1D1D',
              lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 500 }}>Risk: </span>{summary.risk_flag}
            </div>
          )}

          {/* Opportunity */}
          {summary.opportunity && (
            <div style={{
              borderLeft: '3px solid #16A34A',
              background: '#F0FDF4',
              padding: '8px 12px',
              borderRadius: '0 6px 6px 0',
              fontSize: 12,
              color: '#14532D',
              lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 500 }}>Opportunity: </span>{summary.opportunity}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
